import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from './AccessStudio';
import DaycareClassManagerModal from './modals/DaycareClassManagerModal';
import DaycareMenuEditorModal from './modals/DaycareMenuEditorModal';
import DaycareClassReportModal from './modals/DaycareClassReportModal';
import DaycareChildReportModal from './modals/DaycareChildReportModal';

// Daily Report tab: pick a date + class, fill the shared class report and each
// child's report. Teachers see it for their own class; office/admin any class.
// The backend enforces the write scoping. See DAYCARE_DIARY_DESIGN.md.

const today = () => new Date().toISOString().split('T')[0];

const DaycareDailyReport = ({ apiCall, showSuccess, showError }) => {
  const [date, setDate] = useState(today());
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showClasses, setShowClasses] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showClassReport, setShowClassReport] = useState(false);
  const [activeChild, setActiveChild] = useState(null);

  const selectedClass = classes.find((c) => String(c.ClassID) === String(classId));

  const loadClasses = useCallback(async () => {
    try {
      const cls = await apiCall('/daycare/classes?activeOnly=1');
      setClasses(cls || []);
      setClassId((prev) => prev || (cls && cls[0] ? String(cls[0].ClassID) : ''));
    } catch (e) { showError('Error loading classes: ' + e.message); }
  }, [apiCall, showError]);

  const loadRoster = useCallback(async () => {
    if (!classId || !date) { setRoster([]); return; }
    setLoading(true);
    try {
      const r = await apiCall(`/daycare/daily-report/roster?classId=${classId}&date=${date}`);
      setRoster(r || []);
    } catch (e) { showError('Error loading roster: ' + e.message); }
    finally { setLoading(false); }
  }, [apiCall, classId, date, showError]);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => { loadRoster(); }, [loadRoster]);

  const sendAll = async () => {
    if (!classId) return;
    try {
      await apiCall('/daycare/daily-report/class/send-all', { method: 'POST', body: JSON.stringify({ classId: Number(classId), date }) });
      showSuccess('Sent the whole class');
      loadRoster();
    } catch (e) { showError('Error sending class: ' + e.message); }
  };

  const statusChip = (c) => {
    if (c.SentAt) return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Sent</span>;
    if (c.ReportID) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Filled</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Pending</span>;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option value="">Select class…</option>
          {classes.map((c) => <option key={c.ClassID} value={c.ClassID}>{c.ClassName}{c.TeacherName ? ` · ${c.TeacherName}` : ''}</option>)}
        </select>

        <div className="flex-1" />

        <Gate permission="daycare.classes.view" label="Manage Classes">
          <button onClick={() => setShowClasses(true)} className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50">Manage Classes</button>
        </Gate>
        {classId && (
          <>
            <Gate permission="daycare.daily_report.edit" label="Edit Menu">
              <button onClick={() => setShowMenu(true)} className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50">Edit Menu</button>
            </Gate>
            <Gate permission="daycare.daily_report.edit" label="Class Report">
              <button onClick={() => setShowClassReport(true)} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">Class Report</button>
            </Gate>
            <Gate permission="daycare.daily_report.edit" label="Send All">
              <button onClick={sendAll} className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">Send All</button>
            </Gate>
          </>
        )}
      </div>

      {!classId ? (
        <p className="text-sm text-gray-500">Pick a class to see its children.</p>
      ) : loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : roster.length === 0 ? (
        <p className="text-sm text-gray-500">No children assigned to this class. Use “Manage Classes” to assign them.</p>
      ) : (
        <div className="divide-y border rounded">
          {roster.map((c) => {
            // Absent kids get a "we miss you" message, not a daily report.
            const absent = c.Attendance === 'Absent';
            return (
              <div key={c.ChildID} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
                <span className="flex-1 min-w-[140px] text-sm text-gray-800">{c.LastName} {c.FirstName}</span>
                {absent ? (
                  <>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                      💛 We miss you{c.AbsenceReason ? ` · ${c.AbsenceReason}` : ''}
                    </span>
                    <span className="text-xs text-gray-400">absent — no report</span>
                  </>
                ) : (
                  <>
                    {statusChip(c)}
                    <Gate permission="daycare.daily_report.edit" label="Fill Report">
                      <button onClick={() => setActiveChild({ ChildID: c.ChildID, FirstName: c.FirstName, LastName: c.LastName, ClassID: Number(classId) })}
                        className="px-3 py-1 bg-amber-500 text-white rounded text-sm hover:bg-amber-600">
                        {c.ReportID ? 'Edit' : 'Fill'} Report
                      </button>
                    </Gate>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <DaycareClassManagerModal isOpen={showClasses} onClose={() => setShowClasses(false)}
        onSuccess={() => { loadClasses(); loadRoster(); }} apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
      <DaycareMenuEditorModal isOpen={showMenu} onClose={() => setShowMenu(false)} onSuccess={loadRoster}
        classId={Number(classId)} className={selectedClass?.ClassName} date={date} apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
      <DaycareClassReportModal isOpen={showClassReport} onClose={() => setShowClassReport(false)} onSuccess={loadRoster}
        classId={Number(classId)} className={selectedClass?.ClassName} date={date} apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
      <DaycareChildReportModal isOpen={!!activeChild} onClose={() => setActiveChild(null)} onSuccess={loadRoster}
        child={activeChild} date={date} apiCall={apiCall} showSuccess={showSuccess} showError={showError} />
    </div>
  );
};

export default DaycareDailyReport;
